import { FC, MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Share } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, IconButton } from '@mui/material';
import cn from 'classnames';

import InquiryStatus from '@components/InquiryStatus/InquiryStatus';
import { TInquiryStatus } from '@interfaces/inquiries';
import { ISupplementInquiry } from '@interfaces/supplements';
import { getInquiryDetailsUrl, getInquiryResponseUrl, getInquiryUrl } from '@routes/utils';

import { IFoldableInquiryCardProps } from './FoldableInquiryCard.interface';

import './style.scss';

const FoldableInquiryCard: FC<IFoldableInquiryCardProps> = ({
  className,
  inquiryId,
  inquiryName,
  type,
  statusId,
  date,
  items,
  onShare,
  isSupplementary,
}) => {
  const { t } = useTranslation();

  const isResponded = [
    TInquiryStatus.NeedsInfo,
    TInquiryStatus.Complete,
    TInquiryStatus.New,
  ].includes(statusId);

  const hasActiveSupplements = useMemo(() => {
    if (!isSupplementary) {
      return true;
    }
    return (items as ISupplementInquiry[]).some(item => item.isActive);
  }, [items, isSupplementary]);

  const url = getInquiryUrl({
    isResponded,
    inquiryId,
    isSupplementary,
  });

  const handleShare = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      if (onShare) {
        onShare();
      }
    },
    [onShare, inquiryId],
  );

  return (
    <Accordion className={cn('foldable-inquiry-card', className)} disableGutters>
      <AccordionSummary
        className="foldable-inquiry-card__head"
        expandIcon={
          <img className="foldable-inquiry-card__expand-icon" src="/images/img-expand.svg" />
        }
      >
        <div className="foldable-inquiry-card__content">
          <div className="foldable-inquiry-card__name">
            <NavLink className="foldable-inquiry-card__link" to={url}>
              {inquiryName}
            </NavLink>
          </div>

          <InquiryStatus className="foldable-inquiry-card__status" value={statusId} />

          <div className="foldable-inquiry-card__meta">
            <div className="foldable-inquiry-card__type">{type}</div>
            <div className="foldable-inquiry-card__date">{date}</div>
          </div>
        </div>

        {onShare && (
          <IconButton
            className="foldable-inquiry-card__share-button"
            onClick={handleShare}
            disabled={!hasActiveSupplements}
          >
            <Share className="foldable-inquiry-card__share-icon" color="primary" />
          </IconButton>
        )}
      </AccordionSummary>

      <AccordionDetails className="foldable-inquiry-card__body">
        {items.map(item => (
          <div className="foldable-inquiry-card__item" key={item.submittedOn}>
            {item.name}

            {isSupplementary && !(item as ISupplementInquiry).isActive && (
              <div className="foldable-inquiry-card__deactivated">
                {t('dietarySupplementInquiry.deactivated')}
              </div>
            )}
          </div>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default FoldableInquiryCard;
